// Shared geometry preview surface for the map peek popover (T11) and the expanded-dialog map pane.
// Renders the decoded mini map (fit-to-geometry) + summary line + the over-cap "Load full geometry"
// ladder + optional Copy WKT / Copy GeoJSON buttons. All decoding runs through the async lazy API
// (`useSpatialGeometryPreview`); the maplibre map arrives via `SpatialMapHost` (React.lazy), so this
// eager-imported component never statically pulls maplibre / wkx / proj4 into the initial bundle.

import { useMemo } from 'react';
import { MapPin } from 'lucide-react';
import { useAnalytics } from '../../../../core/analytics';
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import { Button } from '../../../../components/ui/button';
import { RandomDotmSpinner } from '../../../../components/ui/RandomDotmSpinner';
import { writeClipboardText } from '../../../../lib/clipboard';
import { summarizeSpatialCell } from '../decode/spatialSniff';
import { formatDeclaredSpatialSummary, type SpatialColumnDeclaration } from '../../common/utils/spatialValueAccessor';
import { loadSpatialDecoder } from '../decode/loadSpatialDecoder';
import { SpatialMapHost, type SpatialMapLayerInput } from './SpatialMapLazy';
import { SpatialCurveApproxHint } from './SpatialCurveApproxHint';
import { SpatialPreviewCopyButtons } from './SpatialPreviewCopyButtons';
import { useSpatialGeometryPreview } from './useSpatialGeometryPreview';

type SpatialGeometryPreviewProps = {
  value: unknown;
  engine: DatabaseEngineId;
  dataType?: string;
  /** T18 — when set, the value is a declared coordinate cell (decoded via the declared arm). */
  declaration?: SpatialColumnDeclaration;
  fetchFullValue?: () => Promise<unknown>;
  /** Gate: only decode when the host surface is open/visible. */
  enabled: boolean;
  /** Render Copy WKT / Copy GeoJSON buttons under the map. */
  showCopy?: boolean;
  /** Fired after a SUCCESSFUL clipboard write (host wires analytics). */
  onCopy?: (format: 'wkt' | 'geojson') => void;
  /** Resolve non-bundled SRIDs from this connection's `spatial_ref_sys` (T12 ladder). */
  connectionId?: string;
  database?: string;
  className?: string;
  /** Collapse the map's attribution to a compact ⓘ (set by the small peek popover surface). */
  compactAttribution?: boolean;
};

export function SpatialGeometryPreview({
  value,
  engine,
  dataType,
  declaration,
  fetchFullValue,
  enabled,
  showCopy = false,
  onCopy,
  connectionId,
  database,
  className,
  compactAttribution,
}: SpatialGeometryPreviewProps) {
  const { track } = useAnalytics();
  const { state, loadFull } = useSpatialGeometryPreview({
    value,
    engine,
    dataType,
    declaration,
    fetchFullValue,
    enabled,
    connectionId,
    database,
  });
  const handleLoadFull = () => {
    track('feature:spatial_load_full', { engine });
    loadFull();
  };

  // Eager summary — for an over-cap value we deliberately did not decode. Declared cells can't run
  // the wire sniffer (cheap, header-only); native cells use it. Reached without the decoder core.
  const sniffSummary = useMemo(
    () =>
      !enabled
        ? null
        : declaration
          ? formatDeclaredSpatialSummary(declaration, value)
          : summarizeSpatialCell(engine, value, dataType),
    [enabled, declaration, engine, value, dataType],
  );

  const layers = useMemo<SpatialMapLayerInput[]>(() => {
    if (state.status !== 'ready') return [];
    return [
      {
        def: {
          id: 'peek',
          label: 'Geometry',
          visible: true,
          source: { kind: 'query', sql: '', geometryRef: { kind: 'column', column: '' } },
          style: { color: '#10b981' },
          featureCap: 1,
        },
        // Reprojected-to-WGS84 (or raw, when planar) features from the T12 ladder.
        features: state.mapFeatures,
      },
    ];
  }, [state]);

  async function handleCopy(format: 'wkt' | 'geojson') {
    if (state.status !== 'ready') return;
    let text: string | null = null;
    if (format === 'geojson') {
      text = JSON.stringify(state.feature.geometry.geojson);
    } else if (declaration) {
      // Declared cells already hold a decoded geometry (a pair's `{ lat, lng }` isn't a wire value),
      // so emit WKT straight from the geojson rather than re-decoding via the engine path (A8/CD5:
      // Copy WKT is the ONLY way a declared pair yields WKT).
      const { toWkt } = await loadSpatialDecoder();
      text = toWkt(state.feature.geometry.geojson, state.feature.geometry.dimensions);
    } else {
      const { toWktForExport } = await loadSpatialDecoder();
      text = toWktForExport(engine, state.value, dataType);
    }
    if (text == null) return;
    const ok = await writeClipboardText(text);
    if (ok) {
      onCopy?.(format);
    }
    // Clipboard blocked — do not report a copy that did not happen.
  }

  return (
    <div className={className} data-testid="spatial-geometry-preview">
      {state.status === 'loading' && (
        <div className="flex h-40 w-full items-center justify-center" data-testid="spatial-peek-loading">
          <RandomDotmSpinner size={22} />
        </div>
      )}

      {state.status === 'over-cap' && (
        <div className="flex flex-col items-center gap-2 py-4 text-center">
          {sniffSummary && (
            <span className="text-xs text-muted-foreground" data-testid="spatial-peek-summary">
              {sniffSummary}
            </span>
          )}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="h-7 text-xs"
            onClick={handleLoadFull}
            data-testid="spatial-peek-load-full"
          >
            <MapPin className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
            Load full geometry ({state.sizeMb})
          </Button>
        </div>
      )}

      {state.status === 'too-large' && (
        <div
          className="py-4 text-center text-xs text-muted-foreground"
          data-testid="spatial-peek-too-large"
        >
          Too large to view ({state.sizeMb})
        </div>
      )}

      {state.status === 'error' && (
        <div className="py-4 text-center text-xs text-muted-foreground" data-testid="spatial-peek-error">
          {state.message}
        </div>
      )}

      {state.status === 'ready' && (
        <>
          <div
            className="h-40 w-full overflow-hidden rounded-md border border-border"
            data-testid="spatial-peek-map"
            // T12 — the SRID-resolution outcome, independent of basemap on/off: 'geographic' means
            // the SRID resolved (features reprojected to WGS84); 'planar' means it fell back to a raw
            // affine. Basemap-off shows a planar STYLE either way, so tests read THIS, not the canvas.
            data-spatial-projection={state.planarTransform ? 'planar' : 'geographic'}
          >
            <SpatialMapHost
              layers={layers}
              planarTransform={state.planarTransform}
              projectionNote={state.projectionNote}
              compactAttribution={compactAttribution}
              fitToData
              embedded
            />
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-2">
            <span className="truncate text-xs text-muted-foreground" data-testid="spatial-peek-summary">
              {state.summary}
            </span>
            {showCopy && <SpatialPreviewCopyButtons onCopy={handleCopy} />}
          </div>
          {state.curveApproximated && <SpatialCurveApproxHint engine={engine} className="mt-1" />}
        </>
      )}
    </div>
  );
}
